import type { MessageDescriptor } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { Chip, type ChipTone } from '../../components/Chip';
import EmptyState from '../../components/EmptyState';
import { Icon } from '../../components/Icon';
import { NavIcon } from '../../components/NavIcon';
import { errorMessage, fetchCsafSourceVulnerabilities } from '../../lib/api';
import { fmtDay, fmtNumber } from '../../lib/format';
import type { CsafSource, CsafSourceVulnerabilitiesResponse } from '../../lib/types';

const PAGE_SIZE = 6;
const SEVERITIES: Readonly<Record<string, { label: MessageDescriptor; tone: ChipTone }>> = {
  CRITICAL: { label: msg`Kritisch`, tone: 'alert' },
  HIGH: { label: msg`Hoch`, tone: 'alert' },
  MEDIUM: { label: msg`Mittel`, tone: 'warn' },
  LOW: { label: msg`Niedrig`, tone: 'good' },
  INFO: { label: msg`Info`, tone: 'neutral' },
  UNASSIGNED: { label: msg`Unbewertet`, tone: 'neutral' },
};

function safeLink(url: string | null): string | undefined {
  return url && (/^https?:\/\//i.test(url) || /^\/(?!\/)/.test(url)) ? url : undefined;
}

/** Fetch only on expansion: large source catalogues never fan out into DT. */
export default function CsafSourceVulnerabilities({ source }: { source: CsafSource }): ReactNode {
  const { t, i18n } = useLingui();
  const sourceName = source.name;
  const panelId = useId();
  const headingId = useId();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [response, setResponse] = useState<CsafSourceVulnerabilitiesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState<{ revision: string; value: number } | null>(null);
  const revision = `${source.name}|${source.last_sync_at ?? ''}`;
  const total = count?.revision === revision ? count.value : null;
  const pendingSearch = draft.trim() !== search;
  const busy = loading || pendingSearch;

  useEffect(() => {
    if (draft.trim() === search) return;
    const timer = window.setTimeout(() => {
      setSearch(draft.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [draft, search]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void fetchCsafSourceVulnerabilities(source.id, page, PAGE_SIZE, search, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const lastPage = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        setResponse(data);
        if (!search) setCount({ revision, value: data.total });
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          setError(errorMessage(err));
          setCount(null);
        }
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [open, source.id, revision, page, search, refresh]);

  const first = response && response.total > 0 ? (response.page - 1) * response.page_size + 1 : 0;
  const last = response ? first + response.items.length - 1 : 0;
  const showSearch = (total !== null && total > 0) || search !== '' || draft !== '';
  const firstLabel = fmtNumber(first);
  const lastLabel = fmtNumber(last);
  const resultTotalLabel = fmtNumber(response?.total);
  const rangeLabel = search
    ? t`${firstLabel}–${lastLabel} von ${resultTotalLabel} Treffern`
    : t`${firstLabel}–${lastLabel} von ${resultTotalLabel} CVEs`;

  return (
    <div className={`csaf-cves${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="csaf-cves-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(value => !value)}
      >
        <span className="csaf-cves-icon"><NavIcon name="database" /></span>
        <span className="csaf-cves-caption">
          <strong id={headingId}><Trans>CVEs in Dependency-Track</Trans></strong>
          <span>
            {total === null
              ? t`Importierte Einträge ansehen`
              : t`${plural(total, { one: '# CVE dieser Quelle', other: '# CVEs dieser Quelle' })}`}
          </span>
        </span>
        {total !== null ? <Chip>{fmtNumber(total)}</Chip> : null}
        <Icon name="chevron-down" size={18} className="csaf-cves-chevron" />
      </button>

      <div id={panelId} role="region" aria-labelledby={headingId} hidden={!open}>
        {open ? <div className="csaf-cves-body">
          <div className="csaf-cves-toolbar">
            <button type="button" className="ghost small" disabled={busy} onClick={() => setRefresh(value => value + 1)} aria-label={t`CVE-Übersicht für ${sourceName} aktualisieren`} title={t`CVE-Übersicht aktualisieren`}><Icon name="refresh" /></button>
          </div>

          {showSearch ? <input
            className="csaf-cves-search"
            type="search"
            aria-label={t`Importierte CVEs von ${sourceName} suchen`}
            placeholder={t`CVE-ID suchen …`}
            value={draft}
            maxLength={100}
            onChange={event => setDraft(event.target.value)}
          /> : null}

          {error ? <div className="csaf-cves-state csaf-cves-error" role="alert">
            <strong><Trans>Übersicht nicht verfügbar</Trans></strong>
            <p>{error}</p>
            <button type="button" className="ghost small" onClick={() => setRefresh(value => value + 1)}><Trans>Erneut versuchen</Trans></button>
          </div> : !response ? <div className="csaf-cves-loading" role="status">
            <span className="csaf-cves-loading-label"><Trans>Gespeicherte CVEs werden geladen …</Trans></span>
            {[0, 1, 2].map(row => <div className="csaf-cves-skeleton" key={row} aria-hidden="true"><span /><span /></div>)}
          </div> : response.items.length === 0 ? <div role="status" aria-busy={busy}>
            <EmptyState
              compact
              title={search ? t`Keine passende CVE gefunden` : t`Noch keine CVEs zugeordnet`}
              action={search ? <button type="button" className="ghost small" onClick={() => setDraft('')}><Trans>Suche zurücksetzen</Trans></button> : null}
            >
              {search
                ? t`Für „${search}“ gibt es in dieser Quelle keinen Treffer.`
                : source.last_sync_at
                  ? t`Der nächste erfolgreiche Lauf dieser Quelle ergänzt die Zuordnung.`
                  : t`Nach dem ersten erfolgreichen Import erscheinen hier die gespeicherten CVEs.`}
            </EmptyState>
          </div> : <>
            <div className="csaf-cves-columns" aria-hidden="true"><span>{busy ? t`Wird aktualisiert …` : 'CVE'}</span><span><Trans>Schweregrad</Trans></span></div>
            <ul className="csaf-cves-list" aria-busy={busy} aria-label={t`In Dependency-Track gespeicherte CVEs von ${sourceName}`}>
              {response.items.map(item => {
                const severity = SEVERITIES[item.severity.toUpperCase()] ?? SEVERITIES.UNASSIGNED!;
                const dtUrl = safeLink(item.dt_url);
                const url = dtUrl ?? `https://nvd.nist.gov/vuln/detail/${encodeURIComponent(item.cve_id)}`;
                const destination = dtUrl ? 'Dependency-Track' : 'NVD';
                const cveId = item.cve_id;
                const publishedDay = item.published ? fmtDay(item.published) : '';
                const detail = item.title || (item.published ? t`Veröffentlicht ${publishedDay}` : null);
                return <li key={item.uuid}>
                  <div className="csaf-cves-entry">
                    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t`${cveId} in ${destination} öffnen (neuer Tab)`} title={t`${cveId} in ${destination} öffnen`}>
                      {item.cve_id}<Icon name="external" size={12} />
                    </a>
                    {detail ? <span className="csaf-cves-detail" title={detail}>{detail}</span> : null}
                  </div>
                  <Chip tone={severity.tone} className={`csaf-cves-severity csaf-cves-severity-${item.severity.toLowerCase()}`}>{i18n._(severity.label)}</Chip>
                </li>;
              })}
            </ul>
            <div className="csaf-cves-pagination">
              <span role="status">
                {busy ? t`CVEs werden aktualisiert …` : rangeLabel}
              </span>
              {response.total > PAGE_SIZE ? <div>
                <button type="button" className="ghost small" disabled={busy || page <= 1} onClick={() => setPage(value => value - 1)} aria-label={t`Vorherige CVE-Seite`}><Icon name="arrow-left" /></button>
                <button type="button" className="ghost small" disabled={busy || page * PAGE_SIZE >= response.total} onClick={() => setPage(value => value + 1)} aria-label={t`Nächste CVE-Seite`}><Icon name="arrow-right" /></button>
              </div> : null}
            </div>
            <p className="csaf-cves-note"><Trans>Gespeicherte CVEs, auch ohne Portfolio-Treffer.</Trans></p>
          </>}
        </div> : null}
      </div>
    </div>
  );
}
